import { useState, useCallback, useRef, useEffect } from 'react'
import { createRoute, Link, useNavigate } from '@tanstack/react-router'
import { projectRoute } from '../../route'
import { Button } from '@/components/ui/button'
import {
  ArrowLeft,
  Ban,
  Calendar,
  Clock,
  FileText,
  Loader2,
  Sparkles,
  Trash2,
  Video,
  Bot,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { orpc } from '@/lib/orpc-client'
import { useDateLocale } from '@/i18n/dates'
import { canJoinNow } from '@hub/contracts'
import { useMeetingCollab } from '@/lib/collab/use-meeting-collab'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'

export const meetingDetailRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/calendar/$meetingId',
  component: MeetingDetailPage,
})

function formatDate(iso: string, intl: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString(intl, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

function formatTime(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  return d.getHours().toString().padStart(2, '0') + ':' + d.getMinutes().toString().padStart(2, '0')
}

function MeetingDetailPage() {
  const { projectId, meetingId } = meetingDetailRoute.useParams()
  const navigate = useNavigate()
  const { t } = useTranslation('meetings')
  const { intl } = useDateLocale()
  const { meeting, updateMeeting, synced } = useMeetingCollab(projectId, meetingId)
  const [editing, setEditing] = useState(false)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [editStartAt, setEditStartAt] = useState('')
  const [editEndAt, setEditEndAt] = useState('')
  const [editMeetingUrl, setEditMeetingUrl] = useState('')
  const [generatingNotes, setGeneratingNotes] = useState(false)

  const handleGenerateNotes = async () => {
    setGeneratingNotes(true)
    try {
      const result = await orpc.meetings.generateNotes({ projectId, meetingId })
      if (result.status === 'no_transcript') {
        alert(t('notes.noTranscript'))
      }
    } catch {
      alert(t('notes.error'))
    } finally {
      setGeneratingNotes(false)
    }
  }

  const startEditing = () => {
    if (!meeting) return
    setEditTitle(meeting.title)
    setEditDescription(meeting.description ?? '')
    setEditStartAt(meeting.startAt ? new Date(meeting.startAt).toISOString().slice(0, 16) : '')
    setEditEndAt(meeting.endAt ? new Date(meeting.endAt).toISOString().slice(0, 16) : '')
    setEditMeetingUrl(meeting.meetingUrl ?? '')
    setEditing(true)
  }

  const handleSave = () => {
    if (!meeting) return
    void updateMeeting({
      ...(editTitle.trim() ? { title: editTitle.trim() } : {}),
      description: editDescription.trim(),
      ...(editStartAt ? { startAt: new Date(editStartAt).toISOString() } : {}),
      ...(editEndAt ? { endAt: new Date(editEndAt).toISOString() } : {}),
      meetingUrl: editMeetingUrl.trim() || null,
    })
    setEditing(false)
  }

  const handleDelete = async () => {
    await orpc.meetings.delete({ projectId, meetingId })
    navigate({ to: '/$projectId/calendar', params: { projectId } })
  }

  if (!synced || !meeting) {
    return (
      <div className="flex items-center justify-center h-full text-muted-foreground">
        {synced ? t('detail.notFound') : t('detail.syncing')}
      </div>
    )
  }

  return (
    <>
      {/* Header */}
      <div className="border-b border-border px-6 py-3 flex items-center gap-4">
        <Link to="/$projectId/calendar" params={{ projectId }}>
          <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t('detail.back')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
        </Link>
        <div className="flex-1">
          <h2
            className={`text-lg font-semibold text-foreground ${meeting.cancelledAt ? 'line-through text-muted-foreground' : ''}`}
          >
            {meeting.title}
          </h2>
        </div>
        <div className="flex items-center gap-2">
          {!editing && (
            <Button variant="outline" size="sm" className="bg-transparent" onClick={startEditing}>
              {t('detail.edit')}
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-muted-foreground hover:text-destructive"
            onClick={handleDelete}
            aria-label={t('detail.delete')}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Cancelled banner */}
      {meeting.cancelledAt && (
        <div className="mx-6 mt-4 flex items-center gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-2.5 text-sm text-destructive">
          <Ban className="h-4 w-4 flex-shrink-0" />
          <span>{t('detail.cancelled')}</span>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-8 py-6 max-w-2xl">
        {editing ? (
          <div className="space-y-4">
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">{t('detail.title')}</label>
              <input
                className="w-full text-sm bg-background border border-border rounded px-3 py-2 outline-none focus:ring-1 focus:ring-ring"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-sm font-medium text-foreground">{t('detail.start')}</label>
                <input
                  type="datetime-local"
                  className="w-full text-sm bg-background border border-border rounded px-3 py-2 outline-none focus:ring-1 focus:ring-ring"
                  value={editStartAt}
                  onChange={(e) => setEditStartAt(e.target.value)}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium text-foreground">{t('detail.end')}</label>
                <input
                  type="datetime-local"
                  className="w-full text-sm bg-background border border-border rounded px-3 py-2 outline-none focus:ring-1 focus:ring-ring"
                  value={editEndAt}
                  onChange={(e) => setEditEndAt(e.target.value)}
                />
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                {t('detail.meetingUrl')}
              </label>
              <input
                type="url"
                className="w-full text-sm bg-background border border-border rounded px-3 py-2 outline-none focus:ring-1 focus:ring-ring"
                value={editMeetingUrl}
                onChange={(e) => setEditMeetingUrl(e.target.value)}
                placeholder={t('detail.meetingUrlPlaceholder')}
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium text-foreground">
                {t('detail.description')}
              </label>
              <textarea
                className="w-full text-sm bg-background border border-border rounded px-3 py-2 outline-none focus:ring-1 focus:ring-ring min-h-24 resize-y"
                value={editDescription}
                onChange={(e) => setEditDescription(e.target.value)}
                placeholder={t('detail.descriptionPlaceholder')}
              />
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" onClick={handleSave}>
                {t('detail.save')}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setEditing(false)}>
                {t('detail.cancel')}
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Meeting info */}
            <div className="flex flex-wrap items-center gap-6 pb-6 border-b border-border">
              {meeting.startAt && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>{formatDate(meeting.startAt, intl)}</span>
                </div>
              )}
              {meeting.startAt && meeting.endAt && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  <span>
                    {formatTime(meeting.startAt)} - {formatTime(meeting.endAt)}
                  </span>
                </div>
              )}
              {meeting.meetingUrl && (
                <a
                  href={meeting.meetingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-sm text-foreground hover:underline"
                >
                  <Video className="h-4 w-4" />
                  <span>{t('detail.meetingUrl')}</span>
                </a>
              )}
              {meeting.botStatus && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Bot className="h-4 w-4" />
                  <span className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs font-medium">
                    {t(`status.${meeting.botStatus}`, { defaultValue: meeting.botStatus })}
                  </span>
                </div>
              )}
              {/* PLAN-085: joining opens from 10 minutes before the start, or
                  any time while the room is live. */}
              {meeting.kind === 'hub' && meeting.roomCode && meeting.roomStatus !== 'ended' && (
                <Button
                  size="sm"
                  className="gap-1.5"
                  disabled={!canJoinNow(meeting.startAt, meeting.roomStatus)}
                  onClick={() =>
                    navigate({
                      to: '/$projectId/meet/$code',
                      params: { projectId, code: meeting.roomCode! },
                    })
                  }
                >
                  <Video className="h-3.5 w-3.5" />
                  {meeting.roomStatus === 'live' ? t('hub.liveBadge') : t('hub.join')}
                </Button>
              )}
            </div>

            {/* Invitees */}
            {meeting.kind === 'hub' && (
              <div>
                <h3 className="text-foreground mb-2 text-sm font-semibold">{t('hub.invitees')}</h3>
                {meeting.invitees.length === 0 ? (
                  <p className="text-muted-foreground text-sm">{t('hub.noInvitees')}</p>
                ) : (
                  <div className="flex flex-wrap gap-3">
                    {meeting.invitees.map((invitee) => (
                      <div key={invitee.userId} className="flex items-center gap-2">
                        <Avatar className="size-6">
                          {invitee.avatarUrl && <AvatarImage src={invitee.avatarUrl} />}
                          <AvatarFallback className="text-[9px]">
                            {invitee.name.slice(0, 1).toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm">{invitee.name}</span>
                      </div>
                    ))}
                  </div>
                )}
                <p className="text-muted-foreground mt-2 text-xs">{t('hub.editNotice')}</p>
              </div>
            )}

            {/* Description */}
            {meeting.description ? (
              <div>
                <h3 className="text-sm font-semibold text-foreground mb-2">
                  {t('detail.description')}
                </h3>
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-wrap">
                  {meeting.description}
                </p>
              </div>
            ) : (
              <p className="text-sm text-muted-foreground italic">{t('detail.noDescription')}</p>
            )}

            {/* Meeting notes */}
            <div className="border-t border-border pt-6">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground">{t('notes.title')}</h3>
                <Button
                  variant="outline"
                  size="sm"
                  className="bg-transparent"
                  onClick={handleGenerateNotes}
                  disabled={generatingNotes}
                >
                  {generatingNotes ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                      {t('notes.generating')}
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3.5 w-3.5 mr-1.5" />
                      {meeting.notesDocumentId ? t('notes.regenerate') : t('notes.generate')}
                    </>
                  )}
                </Button>
              </div>
              {meeting.notesDocumentId ? (
                <Link
                  to="/$projectId/write/$docId"
                  params={{ projectId, docId: meeting.notesDocumentId }}
                  className="inline-flex items-center gap-2 text-sm text-foreground hover:underline"
                >
                  <FileText className="h-4 w-4" />
                  <span>{t('notes.open')}</span>
                </Link>
              ) : (
                <p className="text-sm text-muted-foreground italic">{t('notes.empty')}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}
